<script setup lang="ts">
import { computed, PropType } from "vue";
import { useI18n } from "vue-i18n";
import Card from "primevue/card";
import Button from "primevue/button";
import { Product } from "../../types/TextDiffsTypes.ts";

const props = defineProps({
    selectedProduct: {
        type: Object as PropType<Product | null>,
        default: null,
    },
    selectedLanguage: {
        type: String as () => string | null,
        default: null,
    },
    products: {
        type: Array as () => Product[],
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    showRenderedView: {
        type: Boolean,
        default: false,
    },
    hasData: {
        type: Boolean,
        default: false,
    },
    showEdit: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:selectedProduct", "update:selectedLanguage", "productChange", "languageChange", "toggleRenderedView", "release", "edit"]);

const { t } = useI18n();

const selectedProductModel = computed({
    get: () => props.selectedProduct,
    set: (value) => emit("update:selectedProduct", value),
});

const selectedLanguageModel = computed({
    get: () => props.selectedLanguage,
    set: (value) => emit("update:selectedLanguage", value),
});

function onProductChange() {
    console.log(props.selectedProduct);
    emit("productChange");
}

function onLanguageChange() {
    emit("languageChange");
}

function toggleRenderedView() {
    emit("toggleRenderedView");
}

function onRelease() {
    emit("release");
}

function onEdit() {
    emit("edit");
}
</script>

<template>
    <Card class="product-selector-card mb-6">
        <template #content>
            <div class="selector-grid">
                <div class="product-selector">
                    <label for="product-dropdown" class="selector-label">
                        {{ t("textDiffs.selectProduct") }}
                    </label>
                    <Select
                        id="product-dropdown"
                        v-model="selectedProductModel"
                        filter
                        :options="products"
                        option-label="label"
                        :placeholder="t('textDiffs.selectProductPlaceholder')"
                        class="selector-dropdown"
                        :loading="loading"
                        @change="onProductChange"
                        data-cy="text-diffs_product-selector"
                    />
                </div>
                <div class="language-selector">
                    <label for="language-dropdown" class="selector-label">
                        {{ t("textDiffs.selectLanguage") }}
                    </label>
                    <Select
                        id="language-dropdown"
                        v-model="selectedLanguageModel"
                        filter
                        :options="selectedProduct?.languages || []"
                        option-label="label"
                        option-value="value"
                        :placeholder="t('textDiffs.selectLanguagePlaceholder')"
                        class="selector-dropdown"
                        :disabled="!selectedProduct"
                        @change="onLanguageChange"
                        data-cy="text-diffs_language-selector"
                    />
                </div>
            </div>

            <!-- Release Section -->
            <div class="release-section" v-if="selectedProduct && selectedLanguage && hasData" data-cy="text-diffs_release-section">
                <Button
                    :icon="showRenderedView ? 'pi pi-code' : 'pi pi-eye'"
                    :label="showRenderedView ? t('textDiffs.showHtmlTags') : t('textDiffs.showRendered')"
                    text
                    @click="toggleRenderedView"
                    class="view-toggle-btn"
                    data-cy="text-diffs_view-toggle-button"
                />
                <Button
                    :label="t('textDiffs.releaseChanges')"
                    icon="pi pi-upload"
                    class="release-button"
                    severity="success"
                    @click="onRelease"
                    :disabled="loading"
                    data-cy="text-diffs_release-button"
                />
                        <Button
          v-if="showEdit"
          @click="onEdit"
          icon="pi pi-pencil"
          :label="t('textDiffs.editProduct')"
          class="p-button-secondary"
        />
            </div>
        </template>
    </Card>
</template>

<style scoped>
.selector-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
}

.product-selector,
.language-selector {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.release-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--p-surface-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.release-section .edit-button,
.release-section .release-button {
    flex-shrink: 0;
}

.view-toggle-btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
}

@media (max-width: 768px) {
    .selector-grid {
        grid-template-columns: 1fr;
    }

    .release-section {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }
    
    .release-section .edit-button,
    .release-section .release-button {
        width: 100%;
    }
}
</style>
